import { useState } from 'react'

type Artifact = {
  eyebrow: string
  label: string
  text: string
  output: string
}

function Progress({ visited, total }: { visited: number; total: number }) {
  return (
    <div className="concept-progress" aria-live="polite">
      <span>底稿探索</span><strong>{visited} / {total}</strong>
      <div aria-hidden="true">{Array.from({ length: total }, (_, index) => <i key={index} className={index < visited ? 'is-marked' : ''} />)}</div>
    </div>
  )
}

function ArtifactNote({ artifact }: { artifact: Artifact }) {
  return (
    <div className="concept-note-card" aria-live="polite">
      <span>{artifact.eyebrow}</span>
      <h4>{artifact.label}</h4>
      <p>{artifact.text}</p>
      <small>{artifact.output}</small>
    </div>
  )
}

export function InteractionCue({ label = '点一点' }: { label?: string }) {
  return (
    <div className="interaction-cue" aria-hidden="true">
      <svg viewBox="0 0 36 36">
        <path d="M15 30c-3-4-6-8-8-12-1-2 1-4 3-3l4 4V6c0-3 4-3 4 0v8-2c0-3 4-3 4 0v3-1c0-3 4-3 4 0v3c1-2 4-1 4 2 0 7-4 12-9 13-2 0-4 0-6-2Z" />
        <path d="M7 8 3 5m26 4 4-4M6 25l-4 2" />
      </svg>
      <span>{label}</span>
    </div>
  )
}

const skinArtifacts = {
  brief: { eyebrow: 'BRIEF / 01', label: '先划清产品事实', text: '在谈“不用满分”前，先确认配方证据、宣称边界、首批库存和价格带，避免态度跑在产品前面。', output: '交付物：产品信息底表＋待确认问题清单' },
  mirror: { eyebrow: 'AUDIENCE / 02', label: '用户不是拒绝功效', text: '真正的张力是：用户需要有效护理，却厌倦每天被提醒哪里还不够好。镜子不再负责打分，只负责确认当下感受。', output: '交付物：人群张力卡＋访谈验证题纲' },
  product: { eyebrow: 'PRODUCT / 03', label: '三件产品，各司其职', text: '洁面负责不打扰，精华乳负责日常维稳，修护霜负责状态波动；套组价值来自步骤必要，而不是 SKU 堆叠。', output: '交付物：产品角色图＋卖点优先级' },
  proof: { eyebrow: 'CLAIM / 04', label: '成分语言要有边界', text: '每个核心成分同时写清“能做什么、适合谁、不能承诺什么”，用透明替代过度教育。', output: '交付物：成分 FAQ＋合规用语检查表' },
  trial: { eyebrow: 'TRIAL / 05', label: '七天试用，不给皮肤打分', text: '每天只回答“今天皮肤有没有打扰我”，记录肤感、步骤完成和生活场景，不要求发布前后对比照。', output: '交付物：试用任务卡＋反馈标签表' },
  launch: { eyebrow: 'LAUNCH / 06', label: '上市节奏先解释，再转化', text: '先用价值内容建立“够用主义”，再用产品小抄建立信任，最后由试用反馈和套组页面承接购买。', output: '交付物：四周上市节奏＋渠道物料清单' },
} satisfies Record<string, Artifact>

type SkinKey = keyof typeof skinArtifacts
const skinKeys = Object.keys(skinArtifacts) as SkinKey[]

export function UnfinishedScrapbook() {
  const [active, setActive] = useState<SkinKey>('brief')
  const [visited, setVisited] = useState<SkinKey[]>(['brief'])
  const [mirrorKind, setMirrorKind] = useState<'plain' | 'smile'>('plain')
  const [tubeOpen, setTubeOpen] = useState(false)
  const [trialDay, setTrialDay] = useState(1)

  const activate = (key: SkinKey) => {
    setActive(key)
    setVisited((current) => current.includes(key) ? current : [...current, key])
  }

  return (
    <div className="concept-board concept-board--skin" role="group" aria-label="未满新品上市互动策划工作台">
      <Progress visited={visited.length} total={skinKeys.length} />
      {visited.length < skinKeys.length && <InteractionCue label="来互动一下吧" />}
      <button type="button" className={`skin-brief ${active === 'brief' ? 'is-active' : ''}`} onClick={() => activate('brief')}>
        <span className="interaction-index">01</span>
        <small>LAUNCH BRIEF</small><strong>反焦虑，<br />但不能反功效。</strong><span>拆解 →</span>
      </button>
      <button type="button" className={`skin-mirror ${active === 'mirror' ? 'is-active' : ''}`} onClick={() => { setMirrorKind((value) => value === 'plain' ? 'smile' : 'plain'); activate('mirror') }} aria-label="切换镜子表情并查看人群洞察">
        <span className="interaction-index">02</span>
        <span className={`mirror-face is-${mirrorKind}`} aria-hidden="true"><i /><b /></span><small>NO SCORE</small>
      </button>
      <button type="button" className={`skin-tube ${tubeOpen ? 'is-open' : ''} ${active === 'product' ? 'is-active' : ''}`} onClick={() => { setTubeOpen((value) => !value); activate('product') }}>
        <span className="interaction-index">03</span><span className="tube-number" aria-hidden="true">01</span><strong>留白三件组</strong><i aria-hidden="true" />
      </button>
      <button type="button" className={`skin-proof ${active === 'proof' ? 'is-active' : ''}`} onClick={() => activate('proof')}>
        <span className="interaction-index">04</span>
        <span aria-hidden="true"><i /><i /><i /></span><strong>成分小抄</strong><small>能做 / 不能承诺</small>
      </button>
      <button type="button" className={`skin-trial ${active === 'trial' ? 'is-active' : ''}`} onClick={() => { setTrialDay((value) => value === 7 ? 1 : value + 1); activate('trial') }}>
        <span className="interaction-index">05</span>
        <small>7-DAY TRIAL</small><strong>DAY {trialDay}</strong><span aria-hidden="true">{Array.from({ length: 7 }, (_, index) => <i key={index} className={index < trialDay ? 'is-done' : ''} />)}</span>
      </button>
      <button type="button" className={`skin-launch ${active === 'launch' ? 'is-active' : ''}`} onClick={() => activate('launch')}>
        <span className="interaction-index">06</span>
        <small>W1 → W4</small><strong>上市作战表</strong><span aria-hidden="true">↗</span>
      </button>
      <ArtifactNote artifact={skinArtifacts[active]} />
      {visited.length === skinKeys.length && <div className="concept-complete" role="status">上市底稿集齐!</div>}
    </div>
  )
}

const weekendArtifacts = {
  brief: { eyebrow: 'BRIEF / 01', label: '先定义“无用”的边界', text: '前台可以无结果，后台不能无规则。场地、容量、天气、安全、预算和品牌品类先被锁定。', output: '交付物：活动 Brief＋约束与风险清单' },
  map: { eyebrow: 'JOURNEY / 02', label: '让人自然慢下来的动线', text: '入口先抽取一张无目的任务签，中段进入无结果工作坊和草地休息，离场前才遇到品牌补给，减少商业信息打断体验。', output: '交付物：场地图＋用户旅程＋容量表' },
  task: { eyebrow: 'MECHANISM / 03', label: '抽一件可以不完成的小事', text: '任务足够轻、可随时放弃，也不与赠品挂钩；它只负责给参与者一个离开效率轨道、开始探索现场的理由。', output: '交付物：任务签文案库＋分发与回贴动线' },
  workshop: { eyebrow: 'EXPERIENCE / 04', label: '没结果，也需要好体验', text: '工作坊不设范例和展示墙，但仍需控制每场容量、材料补给、引导语言和清洁周转。', output: '交付物：体验脚本＋分时容量与物料表' },
  brand: { eyebrow: 'PARTNERSHIP / 05', label: '品牌必须成为一种用处', text: '合作方不搭硬广展位，只能提供坐垫、饮品、纸张等休息物件；权益按真实使用而非 Logo 面积设计。', output: '交付物：品牌权益包＋品类排他与露出边界' },
  schedule: { eyebrow: 'OPERATION / 06', label: '随机发生，后台可控', text: '空白节目单只随机内容，不随机安全与容量；现场 Run Sheet 明确每个时段的决策人和升级路径。', output: '交付物：总控表＋人员排班＋天气预案' },
} satisfies Record<string, Artifact>

type WeekendKey = keyof typeof weekendArtifacts
const weekendKeys = Object.keys(weekendArtifacts) as WeekendKey[]

export function WeekendScrapbook() {
  const [active, setActive] = useState<WeekendKey>('brief')
  const [visited, setVisited] = useState<WeekendKey[]>(['brief'])
  const [taskIndex, setTaskIndex] = useState(-1)
  const [kiteFlying, setKiteFlying] = useState(false)
  const [scheduleOpen, setScheduleOpen] = useState(false)

  const activate = (key: WeekendKey) => {
    setActive(key)
    setVisited((current) => current.includes(key) ? current : [...current, key])
  }

  return (
    <div className="concept-board concept-board--weekend" role="group" aria-label="无用周末节互动活动策划工作台">
      <Progress visited={visited.length} total={weekendKeys.length} />
      {visited.length < weekendKeys.length && <InteractionCue label="来互动一下吧" />}
      <button type="button" className={`weekend-brief ${active === 'brief' ? 'is-active' : ''}`} onClick={() => activate('brief')}>
        <span className="interaction-index">01</span>
        <small>EVENT BRIEF</small><strong>前台松弛，<br />后台清醒。</strong><span>打开 →</span>
      </button>
      <button type="button" className={`weekend-map ${active === 'map' ? 'is-active' : ''}`} onClick={() => activate('map')} aria-label="查看活动动线">
        <span className="interaction-index">02</span>
        <svg viewBox="0 0 150 100" aria-hidden="true"><path d="M8 79c20-45 44 8 63-27 17-31 44-6 70-38M25 33h23v22H25zm70 30h33v22H95z" /><circle cx="18" cy="79" r="5" /><circle cx="140" cy="14" r="5" /></svg><small>走一条没目的的路</small>
      </button>
      <button type="button" className={`weekend-task ${taskIndex >= 0 ? 'is-drawn' : ''} ${active === 'task' ? 'is-active' : ''}`} onClick={() => { setTaskIndex((value) => (value + 1) % 3); activate('task') }} aria-label="随机抽取一张无目的任务签">
        <span className="interaction-index">03</span>
        <small>RANDOM TASK</small><strong>{taskIndex < 0 ? '抽一张无目的任务' : ['追一片影子走三分钟', '替陌生人画一朵云', '给今天的风起个名字'][taskIndex]}</strong><span>{taskIndex < 0 ? '抽取 →' : '再抽一张 ↻'}</span>
      </button>
      <button type="button" className={`weekend-kite ${kiteFlying ? 'is-flying' : ''}`} onClick={() => { setKiteFlying((value) => !value); activate('workshop') }} aria-label="放飞风筝并查看体验设计">
        <span className="interaction-index">04</span>
        <span aria-hidden="true" /><i aria-hidden="true" /><small>没结果工作坊</small>
      </button>
      <button type="button" className={`weekend-brand ${active === 'brand' ? 'is-active' : ''}`} onClick={() => activate('brand')}>
        <span className="interaction-index">05</span>
        <span aria-hidden="true" /><strong>品牌无用商店</strong><small>USE, NOT SHOW</small>
      </button>
      <button type="button" className={`weekend-schedule ${scheduleOpen ? 'is-open' : ''} ${active === 'schedule' ? 'is-active' : ''}`} onClick={() => { setScheduleOpen((value) => !value); activate('schedule') }}>
        <span className="interaction-index">06</span>
        <small>RUN SHEET</small><strong>{scheduleOpen ? '14:00 随机散步' : '下一项暂时空白'}</strong><span>翻页 ↗</span>
      </button>
      <ArtifactNote artifact={weekendArtifacts[active]} />
      {visited.length === weekendKeys.length && <div className="concept-complete" role="status">现场底稿集齐!</div>}
    </div>
  )
}
